import { getAccountTasks } from "@/api/tasksAPI";
import { normalizeTaskStatus } from "@/views/Tasks/tasksTableMapper";
import { formatStatusDate } from "@components/utils/formatStatusDate";

export const PHONE_TASK_KIND = Object.freeze({
  UPLOAD: "upload",
  QR: "qr",
  SIMPLE: "simple",
});

const TYPE_LABEL_BY_KIND = Object.freeze({
  [PHONE_TASK_KIND.UPLOAD]: "Upload video",
  [PHONE_TASK_KIND.QR]: "Register QR code",
  [PHONE_TASK_KIND.SIMPLE]: "Task",
});

const TERMINAL_STATUSES = new Set(["completed", "canceled"]);
const PAGE_LIMIT = 100;

export const resolvePhoneTaskKind = (type) => {
  const key = String(type || "").toLowerCase();
  if (key.includes("upload") || key.includes("microlearning")) return PHONE_TASK_KIND.UPLOAD;
  if (key.includes("qr") || key.includes("register")) return PHONE_TASK_KIND.QR;
  return PHONE_TASK_KIND.SIMPLE;
};

export const mapPhoneTask = (raw = {}) => {
  const kind = resolvePhoneTaskKind(raw.type);
  const status = normalizeTaskStatus(raw.status);
  return {
    id: raw.id,
    key: `${raw.type || "task"}-${raw.id}`,
    kind,
    typeLabel: TYPE_LABEL_BY_KIND[kind],
    hasCheckpoint: kind !== PHONE_TASK_KIND.SIMPLE,
    checkpoint: String(raw.criteria || "").trim(),
    responsibility: String(raw.responsibility_name || "").trim(),
    summary: String(raw.summary || "").trim(),
    status,
    isTerminal: TERMINAL_STATUSES.has(status),
    deadline: raw.deadline || null,
    doneOn: raw.done_on || null,
    assignedAt: raw.assigned_at || raw.created_at || "",
  };
};

const assignedTime = (task) => {
  const time = Date.parse(task.assignedAt);
  return Number.isNaN(time) ? 0 : time;
};

/** TO_DO first, then done (COMPLETED + CANCELED); newest assignment first inside each group. */
export const sortPhoneTasks = (tasks = []) =>
  [...tasks].sort(
    (a, b) =>
      Number(a.isTerminal) - Number(b.isTerminal) ||
      assignedTime(b) - assignedTime(a) ||
      (Number(b.id) || 0) - (Number(a.id) || 0)
  );

/** Second line of a list row: "Checkpoint · Responsibility", or the summary for a SIMPLE task. */
export const phoneTaskDetailLine = (task) => {
  if (!task.hasCheckpoint) return task.summary;
  return [task.checkpoint, task.responsibility].filter(Boolean).join(" · ");
};

export const formatPhoneTaskDate = (value) => formatStatusDate(value);

export const getCurrentAccountId = () => {
  try {
    return JSON.parse(localStorage.getItem("currentAccountID"));
  } catch {
    return localStorage.getItem("currentAccountID");
  }
};

/** Every task assigned to the signed-in employee; the mobile list is untruncated. */
export const fetchAllMyTasks = async (accountId) => {
  const collected = [];
  let total = 0;
  do {
    const { data } = await getAccountTasks(accountId, {
      assignee: "me",
      limit: PAGE_LIMIT,
      offset: collected.length,
    });
    const results = Array.isArray(data?.results) ? data.results : [];
    if (!results.length) break;
    collected.push(...results);
    total = typeof data?.count === "number" ? data.count : collected.length;
  } while (collected.length < total);
  return collected;
};
